import React from 'react';
import { Link } from 'react-router-dom';
import { RecentLoad } from '../../../../types/dashboard.types';
import Card, { cardStyles } from '../../../components/ui/Card/Card';
import { ChevronRightIcon, TrailerIcon } from '../../../components/ui/Icons';
import { STATUS_DEFS, canonStatus, statusColorByName } from '../../Loads/statusMeta';
import { isInProgress, progressOf } from './loadProgress';
import styles from './RecentLoadsCard.module.css';

/**
 * LAS ÚLTIMAS CARGAS, PARA QUIEN NO VE *CLIENT ANALYSIS* (20-oct-2026).
 *
 * > *"tengo mis dudas de lo que se estan viendo los clientes en el dashboard... se ve muy vacio para los clientes"*
 *
 * A un cliente el API no le manda `topClientsByVolume` (pide permisos de clientes), y *Client Analysis* salía como un
 * cuadro gris vacío. En su lugar van sus 10 cargas más recientes: el API ya las mandaba (`recentLoads`) y nadie las
 * mostraba.
 *
 * ## 🔄 Rediseño, la misma tarde
 *
 * > *"El concepto esta bien, pero usa alguna skill de ux ui por que esta demasiado simple"*
 *
 * - **Cada carga es un tramo origen → destino, con el camión donde va según su estatus** (`loadProgress`). Es lo que
 *   un cliente viene a saber —dónde está lo suyo— leído de un vistazo, sin abrir la carga.
 * - **El color del estatus tiñe el renglón entero**: la barra de la izquierda, el tramo recorrido y el camión. El
 *   nombre va siempre escrito: el color ayuda a reconocer, la palabra dice qué es.
 * - **Lo de segundo nivel, más chico**: transportista (y el cliente, solo si hay más de uno) y la fecha que importa,
 *   la de entrega si ya se entregó.
 * - Entrada escalonada y el camión "llega" a su punto al cargar; nada se mueve si el sistema pide menos movimiento.
 */

interface RecentLoadsCardProps {
  loads?: RecentLoad[];
}

/** La etiqueta del estatus como en la tabla de cargas ("Waiting Load", no "Waiting to be loaded"). */
const statusLabel = (name?: string): string => {
  if (!name) return 'No status';
  const def = STATUS_DEFS.find((d) => canonStatus(d.key) === canonStatus(name));
  return def ? def.label : name;
};

/**
 * Fecha corta y legible.
 *
 * ⚠️ Una fecha sola (`YYYY-MM-DD`) se lee a mediodía local: como medianoche UTC, en Monterrey saldría el día anterior.
 */
const shortDate = (value?: string): string => {
  if (!value) return '—';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const RecentLoadsCard: React.FC<RecentLoadsCardProps> = ({ loads = [] }) => {
  /** El cliente de cada renglón solo dice algo si hay más de uno; con uno solo, sería su propio nombre diez veces. */
  const manyClients = new Set(loads.map((l) => l.Client?.name).filter(Boolean)).size > 1;
  const inProgress = loads.filter((l) => isInProgress(l.StatusLoad?.name)).length;

  return (
    <Card className={styles.card}>
      <div className={cardStyles.header}>
        <div className={styles.heading}>
          <h3 className={cardStyles.title}>Recent loads</h3>
          <p className={styles.sub}>
            {loads.length
              ? `Your ${loads.length} latest ${loads.length === 1 ? 'shipment' : 'shipments'}`
              : 'Your latest shipments'}
            {inProgress > 0 && <span className={styles.chip}>{inProgress} in progress</span>}
          </p>
        </div>
        <Link className={styles.viewAll} to="/loads">
          View all
          <ChevronRightIcon size={14} aria-hidden="true" />
        </Link>
      </div>

      {loads.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon} aria-hidden="true">
            <TrailerIcon size={22} />
          </span>
          <p className={styles.emptyTitle}>No loads yet</p>
          <p className={styles.emptyText}>Your shipments will show up here as soon as one is created.</p>
        </div>
      ) : (
        <ul className={styles.list}>
          {loads.map((load, i) => {
            const status = load.StatusLoad?.name;
            const progress = progressOf(status);
            const delivered = canonStatus(status) === 'completed' && load.destination_departure;
            const meta = [manyClients ? load.Client?.name : null, load.Carrier?.name || 'No carrier yet']
              .filter(Boolean)
              .join(' · ');

            return (
              <li
                key={load.id}
                className={styles.item}
                style={
                  {
                    '--status': statusColorByName(status),
                    '--progress': `${(progress ?? 0) * 100}%`,
                    /** Techo de 9: más allá la cascada se siente como lentitud, no como entrada. */
                    '--delay': `${Math.min(i, 9) * 45}ms`,
                  } as React.CSSProperties
                }
              >
                <Link className={styles.row} to={`/guide/detail/${load.id}`}>
                  <span className={styles.ident}>
                    <span className={styles.ref}>{load.dylo_reference || load.pro_number || 'No reference'}</span>
                    <span className={styles.meta}>{meta}</span>
                  </span>

                  <span
                    className={[
                      styles.route,
                      progress === undefined ? styles.stopped : '',
                      progress === 1 ? styles.arrived : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <span className={styles.place} title={load.origin || undefined}>
                      {load.origin || '—'}
                    </span>
                    <span className={styles.track} aria-hidden="true">
                      <span className={styles.fill} />
                      {progress !== undefined && (
                        <span className={styles.truck}>
                          <TrailerIcon size={13} />
                        </span>
                      )}
                    </span>
                    <span className={styles.place} title={load.destiny || undefined}>
                      {load.destiny || '—'}
                    </span>
                  </span>

                  <span className={styles.state}>
                    <span className={styles.pill}>{statusLabel(status)}</span>
                    <span className={styles.date}>
                      {delivered
                        ? `Delivered ${shortDate(load.destination_departure)}`
                        : `Created ${shortDate(load.created_date)}`}
                    </span>
                  </span>

                  <ChevronRightIcon className={styles.chevron} size={16} aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

export default RecentLoadsCard;
